import React, { useRef, useState, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { FormBuilder } from 'components/FormBuilder'
import privacyRequestFormJson from './privacyRequestForm.json'
import { generateDynamicYupSchema } from 'components/FormBuilder/generateYupSchemaFromJson'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { CircularProgress, Button, Dialog, IconButton } from '@mui/material'
import { Turnstile } from '@marsidev/react-turnstile'
import { sendPrivacyRightsRequest } from 'services/privacyRights'
import { generateTokenService } from 'services/token'
import { MessageDialog } from 'components/modal/submodals/MessageDialog'
import { UserVerification } from 'components/UserVerification'
import closeLogo from 'images/icon-close.svg'

import 'components/PrivacyRightsRequestForm/PrivacyRightsRequestForm.scss'

const DEFAULT_SUCCESS = {
  title: 'Action confirmed!',
  description:
    'Thank you for submitting your Privacy Rights Request Form. We have received your request and will confirm receipt within 10 business days.',
}

/**
 * @param {object}  props
 * @param {Array}   props.fields        Field schema. Defaults to the bundled JSON, so
 *                                      existing callers are unchanged. Celebrando passes a
 *                                      copy with translated labels — the field `name`s are
 *                                      what the API reads, and those never change.
 * @param {string}  props.submitLabel   Submit button copy.
 * @param {object}  props.success       `{ title, description }` for the confirmation modal.
 */
export default function PrivacyRightsRequest({
  submitButtonClassName = 'request-form-button',
  formClassName = 'request-form',
  fields = privacyRequestFormJson.fields,
  submitLabel = 'Submit',
  success = DEFAULT_SUCCESS,
}) {
  const [registrationError, setRegistrationError] = useState()
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)
  const [verificationData, setVerificationData] = useState(null)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const formDataRef = useRef(null)
  const turnstileRef = useRef(null)

  const jsonValidationYup = useMemo(
    () => generateDynamicYupSchema(fields),
    [fields],
  )

  const [isInProgress, setIsInProgress] = useState(false)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {},
    resolver: useYupValidationResolver(jsonValidationYup),
  })
  const { handleSubmit, setValue, formState, register, reset } = form

  // Custom generate token service for privacy rights (memoized to prevent re-renders)
  const customGenerateToken = useCallback(async payload => {
    return generateTokenService({
      ...payload,
      tokenSource: 'PRIVACY_RIGHTS_REQUEST',
    })
  }, [])

  // Custom validate service that sends the privacy rights request with the token
  const customValidateAndSubmit = useCallback(async ({ token, email }) => {
    const res = await sendPrivacyRightsRequest({
      ...formDataRef.current,
      token,
      email,
    })

    console.log('Privacy rights request response:', res)

    if (res.success) {
      return { success: true }
    }

    // Handle error response with message from API
    const errorMessage =
      res?.message ||
      res?.detail?.message ||
      'Failed to submit privacy rights request'

    console.log('Privacy rights request error message:', errorMessage)

    // Create error object with detail.message for UserVerificationForm error handling
    // This matches the structure expected by UserVerificationForm: error?.detail?.message
    const errorObj = { detail: { message: errorMessage } }
    console.log('Throwing error object:', errorObj)
    throw errorObj
  }, [])

  // Memoize tokenServicePayload to prevent unnecessary re-renders
  const tokenServicePayload = useMemo(
    () => ({
      turnstileToken: verificationData?.turnstileToken,
    }),
    [verificationData?.turnstileToken],
  )

  // Handle verification success - close verification dialog and show success modal
  const handleVerificationSuccess = useCallback(() => {
    setVerificationDialogOpen(false)
    setVerificationData(null)
    setIsInProgress(false)
    setSuccessDialogOpen(true)
    formDataRef.current = null
    reset()
  }, [reset])

  const handleDialogClose = () => {
    setVerificationDialogOpen(false)
    setVerificationData(null)
    setIsInProgress(false)
    turnstileRef.current?.reset()
  }

  // On form submit: open verification dialog (UserVerification will handle token generation)
  const onSubmit = async data => {
    if (!turnstileRef.current?.getResponse()) {
      console.error('Turnstile token not available')
      setRegistrationError('Please complete the verification')
      return
    }

    setIsInProgress(true)
    setRegistrationError(null)

    // Store form data in ref for use in customValidateAndSubmit
    formDataRef.current = data

    // Capture all data needed for verification before opening dialog
    setVerificationData({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      turnstileToken: turnstileRef.current?.getResponse(),
    })

    // Open verification dialog - UserVerification will generate the token
    setVerificationDialogOpen(true)
  }

  return (
    <>
      <FormBuilder
        form={form}
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        fields={fields}
        setFieldValue={setValue}
        errors={registrationError}
        jsonFieldOnForm={'extraField'}
        className={formClassName}
        formHeader={null}
        fieldStyle={{}}
        disabledFields={[]}
      >
        {formState.errors?.server && (
          <div className="error">{formState.errors.server.message}</div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Turnstile
            ref={turnstileRef}
            siteKey={process.env.VISIBLE_TURNSTILE_SITE_KEY}
          />
          <Button
            disabled={isInProgress}
            className={submitButtonClassName}
            variant="contained"
            type="submit"
          >
            {isInProgress ? (
              <CircularProgress color="inherit" size={18} />
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </FormBuilder>
      <Dialog
        onClose={(_, reason) =>
          reason !== 'backdropClick' && handleDialogClose()
        }
        open={verificationDialogOpen}
      >
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            right: 8,
            top: 8,
            alignSelf: 'end',
          }}
        >
          <img src={closeLogo} alt="Close" />
        </IconButton>
        {verificationData && (
          <UserVerification
            hasTurnstile={false}
            onConfirm={handleVerificationSuccess}
            customGenerateTokenService={customGenerateToken}
            customValidateTokenService={customValidateAndSubmit}
            tokenServicePayload={tokenServicePayload}
            email={verificationData.email}
            firstName={verificationData.firstName}
            lastName={verificationData.lastName}
          />
        )}
      </Dialog>
      <MessageDialog
        open={successDialogOpen}
        handleClose={() => setSuccessDialogOpen(false)}
        title={success.title}
        description={success.description}
      />
    </>
  )
}
